import React, { useCallback, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Text from '@/components/Typography/Text';
import { useToolStore, useModalStore } from '@/store';
import { formatCreditBalance } from '@/lib/formatCredits';
import {
  formatTimeAgo,
  getFileUrl,
  getRunStatusVisual,
  getPaletteForTool,
  summarizeRunInputs,
} from '@/components/Tools/toolDetailHelpers';

const EMPTY_RUNS = [];
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Header dropdown listing the user's recent runs for the current tool.
 * Selecting a run drives the tool detail page to switch the visible result
 * via the cross-component bridge (`requestActiveRun` in the store).
 *
 * Designed to live inside the tool detail page's `headerRight`. Always shows
 * the trigger so users have a stable spot to find their history; the
 * dropdown / modal both gracefully handle the empty state.
 *
 * Native split: on iOS / Android we open the shared `ToolPickerModal`
 * instead of a Zeego dropdown — Zeego's UIMenu / PopupMenu don't open
 * reliably from inside the React Navigation header. Web keeps the lighter
 * Radix-backed dropdown since it works correctly there.
 */
function ToolRunsDropdown({ slug }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);

  const tool = useToolStore(s => s.toolsBySlug[slug]);
  const runs = useToolStore(s => s.runsByToolSlug[slug]) || EMPTY_RUNS;
  const requestActiveRun = useToolStore(s => s.requestActiveRun);

  const palette = useMemo(() => getPaletteForTool(tool), [tool]);

  const handleSelect = useCallback((run) => {
    if (!run?.id) return;
    requestActiveRun(slug, run.id);
  }, [requestActiveRun, slug]);

  const handleViewAll = useCallback(() => {
    router.push('/tools/history');
  }, [router]);

  // Show at most 8 in the header dropdown / modal; users can hit "View all
  // runs" for the full history. Keeps the popover compact on small screens.
  const visibleRuns = useMemo(() => runs.slice(0, 8), [runs]);

  // Build the per-run view-model once. Used by both the web dropdown and the
  // native picker modal so we don't reimplement the title/subtitle logic.
  const runItems = useMemo(() => visibleRuns.map((run) => {
    const firstFile = Array.isArray(run.resultFiles) ? run.resultFiles[0] : null;
    const fileUrl   = getFileUrl(firstFile);
    const mime      = firstFile?.mimeType || '';
    const isImage   = mime.startsWith('image/');
    const posterUrl = !isImage
      ? (firstFile?.thumbnailPath || firstFile?.thumbnailUrl || firstFile?.previewUrl || null)
      : null;
    const previewUrl = isImage ? fileUrl : posterUrl;
    const status   = getRunStatusVisual(run.status, colors, palette, t);
    // Title is the user's actual inputs, not the tool name — every row in
    // this list is the same tool, so "AI Text to 3D Model" 4× looks like
    // duplicates. Falling back to tool name only when there are no inputs
    // we can summarize.
    const inputSummary = summarizeRunInputs(run, 60);
    const title = inputSummary
      || run.tool?.name
      || tool?.name
      || run.toolSlug
      || (t('pages.tools.preset') || 'Run');
    const subtitleParts = [status.label, formatTimeAgo(run.createdAt)];
    if (run.creditCost != null && Number(run.creditCost) > 0) {
      subtitleParts.push(`${formatCreditBalance(run.creditCost)} cr`);
    }
    return {
      run,
      title,
      subtitle: subtitleParts.join(' · '),
      icon: status.icon,
      imageUrl: previewUrl || null,
    };
  }), [visibleRuns, colors, palette, t, tool]);

  const runsCountWord = runs.length === 1
    ? (t('pages.tools.runsCountSingle') || '%COUNT% run')
    : (t('pages.tools.runsCount')       || '%COUNT% runs');
  const headerKicker = runs.length > 0
    ? runsCountWord.replace('%COUNT%', runs.length)
    : (t('pages.tools.noRunsYet') || 'No runs yet.');

  // Native: tap → openModal. Same items as the web dropdown plus a
  // "View all runs" footer action that bounces to /tools/history.
  const handleNativePress = useCallback(() => {
    openModal('ToolPickerModal', {
      title: t('pages.tools.recentRuns') || 'Recent runs',
      subtitle: headerKicker,
      emptyText: t('pages.tools.noRunsHint')
        || 'No runs yet. Your past results will appear here so you can revisit, re-run, or continue them in chat.',
      items: runItems.map((item) => ({
        key: item.run.id,
        title: item.title,
        subtitle: item.subtitle,
        icon: item.icon,
        imageUrl: item.imageUrl,
        onSelect: () => handleSelect(item.run),
      })),
      footerAction: {
        title: t('pages.tools.viewAllRuns') || 'View all runs',
        icon: 'clock-rotate-left',
        onPress: handleViewAll,
      },
    });
  }, [openModal, t, headerKicker, runItems, handleSelect, handleViewAll]);

  // ---------- Native (iOS / Android) ----------
  if (IS_TOUCH_NATIVE) {
    return (
      <NavigationBarButton
        icon='clock-rotate-left'
        tooltip={t('pages.tools.headerRunsTooltip') || 'Recent runs'}
        accessibilityLabel={t('pages.tools.headerRunsTooltip') || 'Recent runs'}
        onPress={handleNativePress}
      />
    );
  }

  // ---------- Web / macOS ----------
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <NavigationBarButton
          icon='clock-rotate-left'
          tooltip={t('pages.tools.headerRunsTooltip') || 'Recent runs'}
          accessibilityLabel={t('pages.tools.headerRunsTooltip') || 'Recent runs'}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className='DropdownMenuContent'
        align='end'
        sideOffset={6}
        style={{ minWidth: 280 }}
      >
        <DropdownMenuLabel
          text={runs.length > 0
            ? `${t('pages.tools.recentRuns') || 'Recent runs'} · ${runsCountWord.replace('%COUNT%', runs.length)}`
            : (t('pages.tools.recentRuns') || 'Recent runs')}
        />

        {runItems.length === 0 ? (
          <View style={{ padding: 14 }}>
            <Text size='s' color='secondary'>
              {t('pages.tools.noRunsHint')
                || 'No runs yet. Your past results will appear here so you can revisit, re-run, or continue them in chat.'}
            </Text>
          </View>
        ) : (
          runItems.map((item) => (
            <DropdownMenuItem
              key={item.run.id}
              icon={item.imageUrl ? undefined : item.icon}
              iosIcon={item.icon}
              imageUrl={item.imageUrl || undefined}
              title={item.title}
              subtitle={item.subtitle}
              onSelect={() => handleSelect(item.run)}
            />
          ))
        )}

        <DropdownMenuItemDivider />
        <DropdownMenuItem
          icon='clock-rotate-left'
          iosIcon='clock'
          title={t('pages.tools.viewAllRuns') || 'View all runs'}
          onSelect={handleViewAll}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(ToolRunsDropdown);
